import { resolveNotaryRefreshTokenFromSession } from "@Front/Api/helpers/authTokenResolutionHelpers";
import { getStripeInstance } from "@Front/Api/LeCoffreApi/Admin/Stripe/StripeFactory";
import { LoggerService } from "@Front/Services/LoggerService/LoggerService";
import { TokenRefreshService } from "@Front/Services/TokenRefreshService/TokenRefreshService";
import { getActiveOfficeStore } from "@Front/Stores/ActiveOfficeStore";

type RefreshNotaryEntitlementSessionParams = {
	userUid: string | null | undefined;
	refreshPermissions: () => Promise<void>;
	logLabel: string;
	/** Sync subscription period / seats from Stripe for JWT active office (Mon compte refresh). */
	reconcileStripeOffice?: boolean;
	/** Skip JWT refresh (e.g. just after oauth-exchange — tokens already fresh). */
	skipJwtRefresh?: boolean;
};

async function tryRefreshJwtAfterEntitlementChange(logLabel: string): Promise<void> {
	const refreshToken = resolveNotaryRefreshTokenFromSession();
	if (!refreshToken) {
		return;
	}
	const tokenOutcome = await TokenRefreshService.refreshUserToken(refreshToken);
	if (!tokenOutcome.success) {
		LoggerService.warn(`[${logLabel}] Token refresh after entitlement change failed`, { tokenOutcome });
	}
}

async function tryReconcileStripeOfficeAfterEntitlementChange(logLabel: string): Promise<void> {
	try {
		await getStripeInstance().reconcileOffice();
	} catch (error: unknown) {
		LoggerService.warn(`[${logLabel}] Stripe office reconcile after entitlement change failed`, { error });
	}
}

async function tryReloadUserOfficesAfterEntitlementChange(
	logLabel: string,
	userUid: string | null | undefined,
): Promise<void> {
	const normalizedUserUid = typeof userUid === "string" ? userUid.trim() : "";
	if (normalizedUserUid.length === 0) {
		return;
	}
	try {
		await getActiveOfficeStore().loadUserOffices(normalizedUserUid);
	} catch (error: unknown) {
		LoggerService.warn(`[${logLabel}] loadUserOffices after entitlement change failed`, {
			error,
			userUid: normalizedUserUid,
		});
	}
}

async function tryRefreshPermissionsAfterEntitlementChange(
	logLabel: string,
	refreshPermissions: () => Promise<void>,
): Promise<void> {
	try {
		await refreshPermissions();
	} catch (error: unknown) {
		LoggerService.warn(`[${logLabel}] permissions refresh after entitlement change failed`, { error });
	}
}

/**
 * Refresh JWT (subscription / visitor flags), office affiliations and permissions matrix
 * after entitlement change (subscription, seat, V1 sync).
 */
export async function refreshNotaryEntitlementSession(params: RefreshNotaryEntitlementSessionParams): Promise<void> {
	const { userUid, refreshPermissions, logLabel, reconcileStripeOffice = false, skipJwtRefresh = false } = params;
	if (!skipJwtRefresh) {
		await tryRefreshJwtAfterEntitlementChange(logLabel);
	}
	if (reconcileStripeOffice) {
		await tryReconcileStripeOfficeAfterEntitlementChange(logLabel);
	}
	await tryReloadUserOfficesAfterEntitlementChange(logLabel, userUid);
	await tryRefreshPermissionsAfterEntitlementChange(logLabel, refreshPermissions);
}
